<template>
  <div>
    <!-- FT-102 (REQ-06): фасад «Допы» — CRUD bookable-позиций склада без захода
         в склад-экран. Записи = те же store_products (bookable=true), в складе
         видны со всеми операциями. -->
    <v-row class="mt-1" align="center">
      <v-col cols="12" sm="8">
        <h3>{{ $t("bookable_addons.title") }}</h3>
        <p class="grey--text text--darken-1 mb-0">{{ $t("bookable_addons.subtitle") }}</p>
      </v-col>
      <v-col cols="12" sm="4" class="text-right">
        <v-btn color="primary" small @click="openCreate()">
          <v-icon left small>mdi-plus</v-icon>{{ $t("bookable_addons.add") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table :headers="headers" :items="addons" :loading="loading"
      :items-per-page="25" hide-default-footer class="mt-2" :no-data-text="$t('bookable_addons.empty')">
      <template v-slot:item.name="{ item }">
        <v-avatar size="28" class="mr-2"
          :color="item.image_url && !brokenImages[item.id] ? undefined : ($vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-3')">
          <img v-if="item.image_url && !brokenImages[item.id]" :src="item.image_url" @error="$set(brokenImages, item.id, true)" />
          <v-icon v-else small :color="$vuetify.theme.dark ? 'grey lighten-1' : 'grey darken-1'">
            mdi-package-variant-closed
          </v-icon>
        </v-avatar>
        {{ item.name }}
      </template>
      <template v-slot:item.pricing_mode="{ item }">
        {{ item.pricing_mode === "per_booking" ? $t("bookable_addons.per_booking") : $t("bookable_addons.per_unit") }}
      </template>
      <template v-slot:item.track_availability="{ item }">
        <span v-if="item.track_availability">{{ item.stock_balance }}</span>
        <span v-else class="grey--text">{{ $t("bookable_addons.service") }}</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn v-if="item.track_availability" icon small :title="$t('bookable_addons.stock_in')"
          @click="changeStock(item, 1)"><v-icon small>mdi-plus-box</v-icon></v-btn>
        <v-btn v-if="item.track_availability" icon small :title="$t('bookable_addons.stock_out')"
          @click="changeStock(item, -1)"><v-icon small>mdi-minus-box</v-icon></v-btn>
        <v-btn icon small @click="openEdit(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
        <v-btn icon small @click="removeAddon(item)"><v-icon small>mdi-delete</v-icon></v-btn>
      </template>
    </v-data-table>

    <!-- Приход/списание: Vuetify-диалог, не нативный prompt -->
    <v-dialog v-model="stockDialog.show" max-width="380">
      <v-card>
        <v-card-title>
          {{ stockDialog.direction > 0 ? $t("bookable_addons.stock_in") : $t("bookable_addons.stock_out") }}
          <span v-if="stockDialog.item" class="ml-1">— {{ stockDialog.item.name }}</span>
        </v-card-title>
        <v-card-text>
          <v-text-field v-model.number="stockDialog.qty" type="number" min="1" autofocus
            :label="$t('bookable_addons.quantity_label')"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="stockDialog.show = false">{{ $t("custom.close") }}</v-btn>
          <v-btn color="primary" :disabled="!stockDialog.qty || stockDialog.qty <= 0"
            @click="submitStockChange()">{{ $t("custom.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ editingId ? $t("bookable_addons.edit") : $t("bookable_addons.new_item") }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.name" :label="$t('bookable_addons.name')" required></v-text-field>
          <v-textarea v-model="form.description" :label="$t('bookable_addons.description')" rows="2"></v-textarea>
          <v-row>
            <v-col cols="6">
              <v-text-field v-model.number="form.rental_price" type="number" min="0"
                :label="$t('bookable_addons.price')"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select v-model="form.pricing_mode" :items="pricingModes"
                :label="$t('bookable_addons.pricing_mode')"></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field v-model.number="form.deposit" type="number" min="0"
                :label="$t('bookable_addons.deposit')"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-if="!editingId && form.track_availability" v-model.number="form.initial_quantity"
                type="number" min="0" :label="$t('bookable_addons.initial_quantity')"></v-text-field>
            </v-col>
          </v-row>
          <v-switch v-model="form.track_availability" :label="$t('bookable_addons.track_availability')"
            :hint="$t('bookable_addons.track_availability_hint')" persistent-hint></v-switch>
          <v-file-input v-model="form.image" :label="$t('bookable_addons.photo')" accept="image/*"
            prepend-icon="mdi-camera" dense class="mt-3"></v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">{{ $t("custom.close") }}</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!form.name" @click="save()">
            {{ $t("custom.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { detectConvertible, convertToJpeg } from "@/utils/imageToJpeg";

export default {
  name: "BookableAddonsTab",
  data() {
    return {
      // Картинка допа, которую браузер не декодирует (HEIC/TIFF), — иконка вместо битой картинки.
      brokenImages: {},
      addons: [],
      loading: false,
      saving: false,
      dialog: false,
      stockDialog: { show: false, item: null, direction: 1, qty: 1 },
      editingId: null,
      form: this.emptyForm(),
    };
  },
  computed: {
    authHeaders() {
      return { headers: { Authorization: this.$store.getters.getAuthToken } };
    },
    headers() {
      return [
        { text: this.$t("bookable_addons.name"), value: "name" },
        { text: this.$t("bookable_addons.price"), value: "rental_price" },
        { text: this.$t("bookable_addons.pricing_mode"), value: "pricing_mode" },
        { text: this.$t("bookable_addons.deposit"), value: "deposit" },
        { text: this.$t("bookable_addons.stock"), value: "track_availability" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ];
    },
    pricingModes() {
      return [
        { text: this.$t("bookable_addons.per_unit"), value: "per_unit" },
        { text: this.$t("bookable_addons.per_booking"), value: "per_booking" },
      ];
    },
  },
  created() {
    this.fetchAddons();
  },
  methods: {
    emptyForm() {
      return {
        name: "", description: "", rental_price: 0, pricing_mode: "per_unit",
        deposit: 0, track_availability: true, initial_quantity: 0, image: null,
      };
    },
    fetchAddons() {
      this.loading = true;
      // Каталог без дат: available нам тут не нужен, но stock_balance нужен — берём склад-index и фильтруем bookable
      return this.axios
        .get(`/api/v1/store_products`, this.authHeaders)
        .then((response) => {
          this.addons = (response.data.products || []).filter((p) => p.bookable);
        })
        .catch((error) => this.setError(error, this.$t("bookable_addons.load_error")))
        .finally(() => (this.loading = false));
    },
    openCreate() {
      this.editingId = null;
      this.form = this.emptyForm();
      this.dialog = true;
    },
    openEdit(item) {
      this.editingId = item.id;
      this.form = {
        name: item.name, description: item.description, rental_price: item.rental_price,
        pricing_mode: item.pricing_mode, deposit: item.deposit,
        track_availability: item.track_availability, initial_quantity: 0, image: null,
      };
      this.dialog = true;
    },
    async buildFormData() {
      // multipart — фото через существующий Shrine-канал store_products (:image)
      const fd = new FormData();
      const f = this.form;
      fd.append("store_products[name]", f.name);
      fd.append("store_products[description]", f.description || "");
      fd.append("store_products[rental_price]", f.rental_price || 0);
      fd.append("store_products[pricing_mode]", f.pricing_mode);
      fd.append("store_products[deposit]", f.deposit || 0);
      fd.append("store_products[track_availability]", f.track_availability);
      fd.append("store_products[bookable]", true);
      if (f.image) {
        let image = f.image;
        if (await detectConvertible(image)) {
          const converted = await convertToJpeg(image, { quality: 0.92 });
          // Модуль уже сообщил об отказе: исходник сохраняет возможность скачать фото.
          if (converted) image = converted.file;
        }
        fd.append("store_products[image]", image);
      }
      return fd;
    },
    async save() {
      if (this.saving) return;
      this.saving = true;
      try {
        if (this.editingId) {
          await this.axios.patch(`/api/v1/store_products/${this.editingId}`, await this.buildFormData(), this.authHeaders);
        } else {
          const response = await this.axios.post(`/api/v1/store_products`, await this.buildFormData(), this.authHeaders);
          const qty = parseInt(this.form.initial_quantity, 10);
          if (this.form.track_availability && qty > 0) {
            await this.createArrival(response.data.id, qty);
          }
        }
        this.dialog = false;
        this.$notify(this.$t("bookable_addons.saved"), "", "success", 3000);
        await this.fetchAddons();
      } catch (error) {
        this.setError(error, this.$t("bookable_addons.save_error"));
      } finally {
        this.saving = false;
      }
    },
    async ensureLocationId() {
      const response = await this.axios.get(`/api/v1/store_locations`, this.authHeaders);
      const locations = Array.isArray(response.data) ? response.data : response.data.locations || [];
      if (locations.length > 0) return locations[0].id;
      const created = await this.axios.post(
        `/api/v1/store_locations`,
        { store_locations: { name: this.$t("bookable_addons.default_location") } },
        this.authHeaders
      );
      return created.data.id;
    },
    async createArrival(productId, quantity) {
      const locationId = await this.ensureLocationId();
      await this.axios.post(
        `/api/v1/store_arrivals`,
        { store_arrivals: { store_product_id: productId, store_location_id: locationId, quantity: quantity, price: 0 } },
        this.authHeaders
      );
    },
    changeStock(item, direction) {
      this.stockDialog = { show: true, item, direction, qty: 1 };
    },
    async submitStockChange() {
      const item = this.stockDialog.item;
      const direction = this.stockDialog.direction;
      const qty = parseInt(this.stockDialog.qty, 10);
      this.stockDialog.show = false;
      if (!item || !qty || qty <= 0) return;
      try {
        const locationId = await this.ensureLocationId();
        if (direction > 0) {
          await this.axios.post(`/api/v1/store_arrivals`,
            { store_arrivals: { store_product_id: item.id, store_location_id: locationId, quantity: qty, price: 0 } },
            this.authHeaders);
        } else {
          const response = await this.axios.post(`/api/v1/store_departures`,
            { store_departures: { store_product_id: item.id, store_location_id: locationId, quantity: qty } },
            this.authHeaders);
          // FT-102 (DEC-02): сервер помечает списание ниже резерва
          if (response.data && response.data.below_reserved) {
            this.$notify(this.$t("bookable_addons.below_reserved_warning"), "", "warning", 6000);
          }
        }
        await this.fetchAddons();
      } catch (error) {
        this.setError(error, this.$t("bookable_addons.save_error"));
      }
    },
    async removeAddon(item) {
      const confirmed = await this.$confirm({
        title: this.$t("custom.you_sure_title"),
        text: this.$t("bookable_addons.delete_confirm", { name: item.name }),
        icon: "mdi-alert-outline",
        iconColor: "warning",
        confirmButtonText: this.$t("custom.you_sure_yes"),
        cancelButtonText: this.$t("custom.you_sure_no"),
        useFilledButtons: true,
        maxWidth: 450,
      });
      if (!confirmed) return;
      try {
        await this.axios.delete(`/api/v1/store_products/${item.id}`, this.authHeaders);
        this.$notify(this.$t("bookable_addons.deleted"), "", "success", 3000);
        await this.fetchAddons();
      } catch (error) {
        // 422 при строках броней: вывод из оборота вместо удаления (DEC-02)
        if (error.response && error.response.status === 422) {
          const retire = await this.$confirm({
            title: this.$t("custom.you_sure_title"),
            text: this.$t("bookable_addons.retire_instead"),
            icon: "mdi-archive-outline",
            iconColor: "primary",
            confirmButtonText: this.$t("custom.you_sure_yes"),
            cancelButtonText: this.$t("custom.you_sure_no"),
            useFilledButtons: true,
            maxWidth: 450,
          });
          if (retire) {
            const fd = new FormData();
            fd.append("store_products[bookable]", false);
            await this.axios.patch(`/api/v1/store_products/${item.id}`, fd, this.authHeaders);
            this.$notify(this.$t("bookable_addons.retired"), "", "success", 3000);
            await this.fetchAddons();
          }
        } else {
          this.setError(error, this.$t("bookable_addons.save_error"));
        }
      }
    },
    setError(error, message) {
      console.warn(error);
      this.$notify(message, (error.response && error.response.data && error.response.data.error) || "", "error", 5000);
    },
  },
};
</script>
